import type { TIpRangeRefresh, TIpRangeStatus, TRefreshStatus } from './api';

export type TRangeHealth = 'fresh' | 'serving_stale' | 'never';

export interface IRangeVerdict {
  health: TRangeHealth;
  label: string;
  detail: string;
  tone: 'success' | 'motion' | 'danger';
}

// Not `fetchedAt`: a row that never succeeded carries the Unix epoch, not a null.
export const neverFetched = (row: TIpRangeStatus): boolean => row.version === 0;

export const verdictOf = (row: TIpRangeStatus): IRangeVerdict => {
  if (neverFetched(row)) {
    return {
      health: 'never',
      label: 'Never downloaded',
      detail:
        'This list has never been fetched, so it holds no subnets. A source that checks sender addresses against it has nothing to match, and every request fails the check.',
      tone: 'danger',
    };
  }

  if (row.stale) {
    return {
      health: 'serving_stale',
      label: 'Serving the last known list',
      detail:
        'Filtering still runs, but on a list that has gone too long without a successful refresh. Nothing narrowed and nothing broke — the addresses in force are simply the ones from the last time the provider answered.',
      tone: 'motion',
    };
  }

  return {
    health: 'fresh',
    label: 'Up to date',
    detail: 'Downloaded when it was due, from the address the preset publishes.',
    tone: 'success',
  };
};

export const failureNote = (row: TIpRangeStatus): string | null => {
  if (!row.hasError) return null;
  const times = row.consecutiveFailures === 1 ? 'once' : `${row.consecutiveFailures} times`;
  return `Last attempt failed, ${times} in a row`;
};

export interface IOutcomeCopy {
  label: string;
  detail: string;
  tone: 'success' | 'motion' | 'danger';
}

const OUTCOME: Record<TRefreshStatus, IOutcomeCopy> = {
  updated: {
    label: 'Updated',
    detail: 'The provider answered with a different list, and it is now the one in force.',
    tone: 'success',
  },
  unchanged: {
    label: 'Already current',
    detail:
      'The provider answered with the list we already had. The version does not move — only the time of the last successful check does.',
    tone: 'success',
  },
  skipped: {
    label: 'Not due yet',
    detail: 'The schedule has not come round for this list, so nothing was fetched.',
    tone: 'motion',
  },
  failed: {
    label: 'Could not reach the provider',
    detail:
      'The fetch did not work. Nothing was changed on purpose: an allowlist that narrows on a network error starts refusing live traffic.',
    tone: 'danger',
  },
};

export const outcomeCopy = (outcome: TIpRangeRefresh): IOutcomeCopy => OUTCOME[outcome.status];

export const subnetCount = (count: number): string =>
  `${count.toLocaleString('en-US')} subnet${count === 1 ? '' : 's'}`;
